import { Button, Container, IconButton, LinearProgress, Paper, Stack, ThemeProvider, Typography } from "@mui/material";
import { ChevronLeft, MoveRight } from "lucide-react";
import Logo from "@/assets/logos/bitelink-logo-blue-bg.svg";
import { createContext, ReactNode, useState } from "react";
import OrganizationTypeStep from "./steps/OrganizationTypeStep";
import OrganizationNameStep from "./steps/OrganizationNameStep";
import ColorStep from "./steps/ColorStep";
import SocialNetworksStep from "./steps/SocialNetworksStep";
import AuthenticationStep from "./steps/AuthenticationStep";
import LogoStep from "./steps/LogoStep";
import OnboardingTheme from "./OnboardingTheme";
import useRoutes from "@/hooks/useRoutes";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { colorRegex, slugRegex } from "@/constants/global";
import useSnackbar from "@/hooks/useSnackbar";
import OrganizationAliasStep from "./steps/OrganizationAliasStep";

export interface OnboardingFormValues {
  industry: string;
  alias: string;
  title: string;
  primaryColor: string;
  secondaryColor: string;
  facebook?: string;
  instagram?: string;
  x?: string;
  tiktok?: string;
  whatsapp: string;
  isAliasValid: boolean;
  logo: File | null;
  isotype: File | null;
}
export interface Step {
  title?: string;
  label: string;
  component: ReactNode;
  hideContinueButton?: boolean;
  skipable?: boolean;
  schema?: Yup.ObjectSchema<any>;
}

const REQUIRED_MESSAGE = "Para continuar, elige un valor";
const minMessage = (field: string) => `${field} debe contener al menos 3 caracteres`;

const STEPS: Record<string, Step> = {
  industry: {
    label: "Tipo",
    title: "¿Qué tipo de comercio tienes?",
    component: <OrganizationTypeStep />,
    schema: Yup.object().shape({
      industry: Yup.string().required(REQUIRED_MESSAGE).oneOf(["restaurant", "bar", "coffee-shop"], "El tipo de comercio no es válido"),
    }),
  },
  name: {
    label: "Nombre",
    component: <OrganizationNameStep />,
    schema: Yup.object().shape({
      title: Yup.string().required("Para continuar, ingresa un nombre").min(3, minMessage("El nombre")),
    }),
  },
  alias: {
    label: "Dirección",
    title: "¿Cómo quieres que te encuentren?",
    component: <OrganizationAliasStep />,
    schema: Yup.object().shape({
      alias: Yup.string().required("Ingresa el alias (nombre) de tu comercio").min(3, minMessage("La dirección")).matches(slugRegex, "El formato no es correcto"),
      isAliasValid: Yup.boolean().isTrue("La dirección no está disponible, elige otra"),
    }),
  },
  logo: {
    label: "Logotipo",
    component: <LogoStep />,
    skipable: true,
    schema: Yup.object().shape({
      logo: Yup.string().nullable(),
      isotype: Yup.string().nullable(),
    }),
  },
  theme: {
    label: "Apariencia",
    component: <ColorStep />,
    schema: Yup.object().shape({
      secondaryColor: Yup.string().required("Para continuar, elige un color secundario").matches(colorRegex, "El formato no es correcto"),
      primaryColor: Yup.string().required("Para continuar, elige un color primario").matches(colorRegex),
    }),
  },
  networks: {
    label: "Redes sociales",
    title: "¿En qué redes sociales estás?",
    component: <SocialNetworksStep />,
    schema: Yup.object().shape({
      facebook: Yup.string().min(3, minMessage("La dirección de Facebook")),
      instagram: Yup.string().min(3, minMessage("La dirección de Instagram")),
      x: Yup.string().min(3, minMessage("La dirección de X")),
      tiktok: Yup.string().min(3, minMessage("La dirección de TikTok")),
    }),
  },
  authentication: {
    label: "Inicio de sesión",
    title: "¿Cómo deseas iniciar sesión?",
    component: <AuthenticationStep />,
    hideContinueButton: true,
  },
};

interface OnboardingContext {
  steps: Step[];
  validateStep: (stepId: string) => void;
}

export const OnboardingContext = createContext<OnboardingContext>({
  steps: [],
  validateStep: () => undefined,
});

const Onboarding = () => {
  const [navigate] = useRoutes();
  const [openSnackbar] = useSnackbar();
  const [steps] = useState(STEPS);
  const [stepIndex, setStepIndex] = useState(0);

  const activeStep = Object.values(steps)[stepIndex];

  const onContinue = async (values: OnboardingFormValues) => {
    if (activeStep?.schema) {
      try {
        await activeStep.schema.validate(values);
      } catch (error) {
        openSnackbar(error?.message, "error");
        return;
      }
    }

    setStepIndex((prev) => prev + 1);
  };

  const onBack = () => {
    if (stepIndex === 0) {
      if (confirm("¿Deseas regersar al inicio?")) {
        navigate("home");
      }
    } else {
      setStepIndex((prev) => prev - 1);
    }
  };

  const handleSkipStep = () => {
    setStepIndex((prev) => prev + 1);
  };

  if (!activeStep) {
    return null;
  }

  return (
    <ThemeProvider theme={OnboardingTheme}>
      <OnboardingContext.Provider
        value={{
          steps: Object.values(steps),
        }}
      >
        <Formik
          initialValues={{
            industry: "restaurant",
            title: "",
            alias: "",
            primaryColor: "",
            secondaryColor: "",
            whatsapp: "",
            isAliasValid: false,
            logo: null,
            isotype: null,
          }}
          onSubmit={(values: OnboardingFormValues) => {
            const isLastStep = stepIndex >= Object.values(steps).length - 1;
            if (!isLastStep) onContinue(values);
          }}
          enableReinitialize={true}
        >
          <Form style={{ height: "100%" }}>
            <Stack height="100%">
              <Stack
                py={3}
                px={2}
                direction="row"
                sx={{
                  backgroundColor: (theme) => theme.palette.primary.main,
                }}
              >
                <IconButton size="large" elevation={0} component={Paper} sx={{ backgroundColor: "rgba(255, 255, 255, 0.1)" }} onClick={onBack}>
                  <ChevronLeft color="white" />
                </IconButton>
                <Stack alignItems="center" flex={1} pr="48px">
                  <img src={Logo} alt="BiteLink" style={{ height: "43px" }} />
                </Stack>
              </Stack>
              <Stack
                flex={1}
                sx={{
                  background: ({ palette }) => `linear-gradient(180deg, ${palette.primary.dark} 0%,  ${palette.primary.main} 100%)`,
                }}
              >
                <LinearProgress value={((stepIndex + 1) * 100) / Object.values(steps).length} variant="determinate" color="secondary" />
                <Stack flex={1} padding={2} alignItems="center" justifyContent="center">
                  <Container maxWidth="md">
                    <Stack spacing={3}>
                      {activeStep?.title ? (
                        <Typography textAlign="center" variant="h1" color="white">
                          {activeStep?.title}
                        </Typography>
                      ) : null}
                      {activeStep.component ?? null}
                    </Stack>
                  </Container>
                </Stack>
                {stepIndex < Object.values(steps).length - 1 ? (
                  <Stack padding={3} alignItems="center">
                    <Container maxWidth="md">
                      <Stack direction="row" spacing={2}>
                        {activeStep?.skipable ? (
                          <Button
                            fullWidth
                            size="large"
                            color="white"
                            variant="outlined"
                            onClick={handleSkipStep}
                            sx={{
                              borderColor: "white",
                              color: "white",
                            }}
                          >
                            Saltar
                          </Button>
                        ) : null}
                        <Button fullWidth size="large" color="secondary" variant="contained" type="submit">
                          <MoveRight />
                        </Button>
                      </Stack>
                    </Container>
                  </Stack>
                ) : null}
              </Stack>
            </Stack>
          </Form>
        </Formik>
      </OnboardingContext.Provider>
    </ThemeProvider>
  );
};

export default Onboarding;
